import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertCircle, Building, CornerDownLeft, FileText, FolderOpen, Loader2, MapPin,
  Search as SearchIcon, ShoppingCart, Truck, UserRound, Users, Workflow, X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  loadParentProjects, loadPipelineCatalogue, loadStaff, searchEverything,
} from './api';
import { tx } from './i18n';
import { codeLabel, orderReferenceLabel, roleLabel } from './labels';
import { statusLabel } from './ReportParts';
import { errorMessage } from './ui';

// Quick search.
//
// Pages and records in one box, and none of it decides who may see what. The
// pages are the screens this account may open, the same `destinations` the
// sidebar is built from. The records come from searches the server already has:
//
// - Patients, samples, orders and shipments come from /Search, one request per
//   pause in typing, and a newer keystroke cancels the older request. The
//   server checks each source's own permission, scopes it the way that
//   source's own list is scoped, and answers "forbidden" for a source the
//   caller may not read, which is then simply not shown.
// - Projects, pipelines and staff are short lists their own pages already
//   read. They are read once, the first time something is typed, through those
//   same endpoints (same permission, same scoping), and matched here.
//
// A type is offered only when this account can open the page its records land
// on, and that page's permission is the one the type's list route requires.
// A result opens that page with the record open, through the page's own link.

const RECORD_LIMIT = 5;
const PAUSE_MS = 200;

function day(value) {
  if (value === null || value === undefined) return null;
  return new Date(Number(value)).toLocaleDateString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function joined(parts) {
  return parts.filter((part) => part !== null && part !== undefined && part !== '').join(' · ');
}

// Record types in the order they are listed. `page` is the destination the
// record opens in; without it the type is not offered at all. `source` names a
// /Search source, `load` a list read once and matched in the browser.
const TYPES = [
  {
    key: 'samples', page: 'sample-tracking', group: 'ui.search.group_samples', icon: MapPin, source: 'samples',
    row: (sample) => ({
      id: sample.uuid,
      title: tx('ui.search.sample_code', { code: sample.printedCode ?? sample.publicCode }),
      detail: joined([
        sample.organization === null ? null : sample.organization.name,
        sample.activatedAt === null ? null : tx('ui.search.activated_on', { date: day(sample.activatedAt) }),
      ]),
      to: `/xomics/sample-tracking?sampleUuid=${encodeURIComponent(sample.uuid)}`,
    }),
  },
  {
    key: 'orders', page: 'orders', group: 'ui.search.group_orders', icon: ShoppingCart, source: 'orders',
    row: (order) => ({
      id: order.uuid,
      title: tx('ui.search.order_reference', { reference: orderReferenceLabel(order.trackingCode) }),
      detail: joined([
        order.name, order.email, order.countryCode,
        order.createdAt === null ? null : tx('ui.search.placed_on', { date: day(order.createdAt) }),
      ]),
      to: `/xomics/orders?orderUuid=${encodeURIComponent(order.uuid)}`,
    }),
  },
  {
    key: 'patients', page: 'projects', group: 'ui.search.group_patients', icon: UserRound, source: 'patients',
    row: (patient) => ({
      id: String(patient.id),
      title: patient.name || tx('ui.search.patient_number', { id: patient.id }),
      detail: joined([
        tx('ui.search.patient_number', { id: patient.id }),
        patient.email,
        patient.dateOfBirth === null ? null : tx('ui.search.born_on', { date: patient.dateOfBirth }),
      ]),
      to: `/xomics/projects?patient=${encodeURIComponent(patient.id)}`,
    }),
  },
  {
    key: 'reports', page: 'reporting/files', group: 'ui.search.group_reports', icon: FileText, source: 'reports',
    row: (report) => ({
      id: report.uuid,
      title: report.patientName || report.reportType || tx('ui.search.report'),
      detail: joined([
        report.patientName === null ? null : report.reportType,
        report.pipelineName,
        statusLabel(report.status),
        report.requestedAt === null ? null : tx('ui.search.requested_on', { date: day(report.requestedAt) }),
      ]),
      to: `/xomics/reporting/files?q=${encodeURIComponent(report.uuid)}`,
    }),
  },
  {
    key: 'staff', page: 'staff', group: 'ui.search.group_staff', icon: Users,
    load: async () => (await loadStaff()).staff,
    words: (person) => [
      `${person.firstName} ${person.lastName}`, person.email, roleLabel(person.role.code),
      person.location.name, person.location.code,
    ],
    row: (person) => ({
      id: person.uuid,
      title: `${person.firstName} ${person.lastName}`,
      detail: joined([person.email, roleLabel(person.role.code), person.location.name]),
      to: `/xomics/staff?account=${encodeURIComponent(person.uuid)}`,
    }),
  },
  {
    key: 'projects', page: 'projects', group: 'ui.search.group_projects', icon: FolderOpen,
    load: () => loadParentProjects(),
    words: (project) => [project.name, String(project.id)],
    row: (project) => ({
      id: String(project.id),
      title: project.name,
      detail: tx('ui.search.project_number', { id: project.id }),
      to: `/xomics/projects?project=${encodeURIComponent(project.id)}`,
    }),
  },
  {
    key: 'organizations', page: 'organizations', group: 'ui.search.group_organizations', icon: Building, source: 'organizations',
    row: (organization) => ({
      id: String(organization.id),
      title: organization.name,
      detail: joined([
        organization.code,
        organization.countryCode,
        organization.isActive ? null : tx('ui.search.inactive'),
      ]),
      to: `/xomics/organizations?q=${encodeURIComponent(organization.name)}`,
    }),
  },
  {
    key: 'pipelines', page: 'pipelines/catalogue', group: 'ui.search.group_pipelines', icon: Workflow,
    load: async () => (await loadPipelineCatalogue({ scope: 'active' })).pipelines,
    words: (pipeline) => [
      pipeline.displayName, pipeline.name, pipeline.mode, pipeline.codeVersion, pipeline.omics,
    ],
    row: (pipeline) => ({
      id: String(pipeline.id),
      title: pipeline.displayName,
      detail: joined([
        pipeline.name, pipeline.mode,
        pipeline.codeVersion ? tx('ui.search.version', { version: pipeline.codeVersion }) : null,
        pipeline.omics,
      ]),
      to: `/xomics/pipelines/catalogue?pipeline=${encodeURIComponent(pipeline.id)}`,
    }),
  },
  {
    key: 'shipments', page: 'shipments', group: 'ui.search.group_shipments', icon: Truck, source: 'shipments',
    row: (shipment) => ({
      id: shipment.uuid,
      title: shipment.toName
        ? tx('ui.search.shipment_to', { name: shipment.toName }) : tx('ui.search.shipment'),
      detail: joined([
        codeLabel('shipmentStatus', shipment.status),
        shipment.trackingNumber,
        shipment.createdAt === null ? null : tx('ui.search.created_on', { date: day(shipment.createdAt) }),
      ]),
      to: `/xomics/shipments?shipmentUuid=${encodeURIComponent(shipment.uuid)}`,
    }),
  },
];

// 0 for a value that starts with the text, 1 for a word inside it that does,
// 2 for anywhere else, null for no match. Lower sorts first.
function rank(words, needle) {
  let best = null;
  for (const word of words) {
    if (word === null || word === undefined) continue;
    const text = String(word).toLowerCase();
    let score = null;
    if (text.startsWith(needle)) score = 0;
    else if (text.split(/\s+/).some((part) => part.startsWith(needle))) score = 1;
    else if (text.includes(needle)) score = 2;
    if (score !== null && (best === null || score < best)) best = score;
  }
  return best;
}

function matchList(type, items, needle) {
  const matches = items
    .map((item) => ({ item, score: rank(type.words(item), needle) }))
    .filter((match) => match.score !== null)
    .sort((left, right) => left.score - right.score);
  return {
    items: matches.slice(0, RECORD_LIMIT).map((match) => match.item),
    hasMore: matches.length > RECORD_LIMIT,
  };
}

export function useSearchHotkey(onOpen) {
  useEffect(() => {
    function onKeyDown(event) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        onOpen();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onOpen]);
}

export function SearchOverlay({ destinations, onClose }) {
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [cursor, setCursor] = useState(0);
  // The last /Search answer is kept while the next one loads, so the list does
  // not flash empty between keystrokes.
  const [remote, setRemote] = useState({ status: 'idle', sources: null, error: '' });
  const [lists, setLists] = useState({});
  const inputRef = useRef(null);
  const bodyRef = useRef(null);
  const requested = useRef(new Set());
  const alive = useRef(true);

  useEffect(() => { inputRef.current.focus(); }, []);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const open = useMemo(() => new Set(destinations.map((destination) => destination.path)), [destinations]);
  const types = useMemo(() => TYPES.filter((type) => open.has(type.page)), [open]);
  const asksServer = types.some((type) => type.source !== undefined);
  const needle = term.trim().toLowerCase();
  const query = term.trim();

  useEffect(() => {
    if (query === '' || !asksServer) {
      setRemote({ status: 'idle', sources: null, error: '' });
      return undefined;
    }
    setRemote((current) => ({ ...current, status: 'loading', error: '' }));
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      searchEverything(query, { limit: RECORD_LIMIT, signal: controller.signal })
        .then((result) => {
          if (!controller.signal.aborted) setRemote({ status: 'ready', sources: result.sources, error: '' });
        })
        .catch((error) => {
          if (controller.signal.aborted) return;
          if (error.status === 400 && error.code === 'QUERY_TOO_SHORT') {
            setRemote({ status: 'short', sources: null, error: '' });
            return;
          }
          setRemote({ status: 'failed', sources: null, error: errorMessage(error) });
        });
    }, PAUSE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [query, asksServer]);

  const typing = needle !== '';
  useEffect(() => {
    if (!typing) return;
    types.filter((type) => type.load !== undefined).forEach((type) => {
      if (requested.current.has(type.key)) return;
      requested.current.add(type.key);
      setLists((current) => ({ ...current, [type.key]: { status: 'loading', items: [], error: '' } }));
      type.load()
        .then((items) => {
          if (alive.current) setLists((current) => ({ ...current, [type.key]: { status: 'ready', items, error: '' } }));
        })
        .catch((error) => {
          if (alive.current) {
            setLists((current) => ({
              ...current, [type.key]: { status: 'failed', items: [], error: errorMessage(error) },
            }));
          }
        });
    });
  }, [typing, types]);

  const groups = useMemo(() => {
    const pages = needle === '' ? destinations : destinations.filter((destination) => (
      destination.label.toLowerCase().includes(needle)
      || destination.section.toLowerCase().includes(needle)
      || (destination.parent !== null && destination.parent.toLowerCase().includes(needle))
      || destination.keywords.some((word) => word.includes(needle))
    ));
    const built = [];
    if (pages.length > 0) {
      built.push({
        key: 'pages',
        label: tx('ui.search.group_pages'),
        note: null,
        rows: pages.map((destination) => ({
          key: `page:${destination.path}`,
          icon: destination.icon,
          title: destination.label,
          detail: destination.parent === null
            ? destination.section
            : tx('ui.search.section_parent_81f10e97', { section: destination.section, parent: destination.parent }),
          to: destination.path,
        })),
      });
    }
    if (needle === '') return built;

    types.forEach((type) => {
      let found = null;
      let note = null;
      if (type.source !== undefined) {
        const source = remote.sources === null ? undefined : remote.sources[type.source];
        if (source === undefined || source.status === 'forbidden') return;
        if (source.status === 'error') note = { failed: true, text: tx('ui.search.source_failed') };
        else found = { items: source.items, hasMore: source.hasMore };
      } else {
        const state = lists[type.key];
        if (state === undefined || state.status === 'loading') return;
        if (state.status === 'failed') note = { failed: true, text: tx('ui.search.list_failed', { message: state.error }) };
        else found = matchList(type, state.items, needle);
      }
      if (found !== null && found.items.length === 0) return;
      if (found !== null && found.hasMore) note = { failed: false, text: tx('ui.search.more_matches') };
      built.push({
        key: type.key,
        label: tx(type.group),
        note,
        rows: found === null ? [] : found.items.map((item) => {
          const row = type.row(item);
          return { ...row, key: `${type.key}:${row.id}`, icon: type.icon };
        }),
      });
    });
    return built;
  }, [destinations, lists, needle, remote.sources, types]);

  const rows = useMemo(() => groups.flatMap((group) => group.rows), [groups]);
  const busy = remote.status === 'loading'
    || Object.values(lists).some((state) => state.status === 'loading');

  // The highlighted row has to stay inside a list that changes as you type.
  const active = Math.min(cursor, Math.max(0, rows.length - 1));

  useEffect(() => {
    const node = bodyRef.current === null ? null : bodyRef.current.querySelector(`[data-row="${active}"]`);
    if (node !== null && typeof node.scrollIntoView === 'function') node.scrollIntoView({ block: 'nearest' });
  }, [active]);

  function go(row) {
    navigate(row.to);
    onClose();
  }

  function onKeyDown(event) {
    if (event.key === 'Escape') { onClose(); return; }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setCursor(Math.min(active + 1, rows.length - 1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setCursor(Math.max(active - 1, 0));
      return;
    }
    if (event.key === 'Enter' && rows.length > 0) {
      event.preventDefault();
      go(rows[active]);
    }
  }

  let index = -1;
  return createPortal(
    <div id="xo" className="xo-overlay">
      <button type="button" className="xo-scrim" aria-label={tx("ui.search.close_search_0906f923")} onClick={onClose} />
      <div className="xo-modal" role="dialog" aria-modal="true" aria-label={tx("ui.search.quick_search_3c4cdb32")}>
        <div className="xo-modal-search">
          {busy ? <Loader2 className="xo-spin" aria-hidden="true" /> : <SearchIcon aria-hidden="true" />}
          <input
            ref={inputRef}
            value={term}
            placeholder={tx('ui.search.search_pages_and_records')}
            aria-label={tx('ui.search.search_pages_and_records_label')}
            aria-busy={busy}
            onChange={(event) => { setTerm(event.target.value); setCursor(0); }}
            onKeyDown={onKeyDown}
          />
          <button type="button" className="xo-modal-x" aria-label={tx("ui.search.close_search_0906f923")} onClick={onClose}>
            <X aria-hidden="true" />
          </button>
        </div>

        <div className="xo-modal-body" ref={bodyRef}>
          {remote.status === 'failed' && (
            <p className="xo-hit-note t-bad" role="alert">
              <AlertCircle aria-hidden="true" />{tx('ui.search.records_failed', { message: remote.error })}
            </p>
          )}
          {groups.map((group) => (
            <section key={group.key} className="xo-hit-group" aria-label={group.label}>
              <p className="xo-hit-group-head">{group.label}</p>
              {group.rows.length > 0 && (
                <ul className="xo-hits">
                  {group.rows.map((row) => {
                    index += 1;
                    const at = index;
                    const Icon = row.icon;
                    return (
                      <li key={row.key}>
                        <button
                          type="button"
                          data-row={at}
                          className={at === active ? 'is-on' : ''}
                          onMouseEnter={() => setCursor(at)}
                          onClick={() => go(row)}
                        >
                          <span className="xo-hit-icon" aria-hidden="true"><Icon /></span>
                          <span className="xo-lines">
                            <strong>{row.title}</strong>
                            {row.detail !== '' && <span>{row.detail}</span>}
                          </span>
                          {at === active && <CornerDownLeft aria-hidden="true" />}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {group.note !== null && (
                <p className={`xo-hit-note${group.note.failed ? ' t-bad' : ''}`}>
                  {group.note.failed && <AlertCircle aria-hidden="true" />}{group.note.text}
                </p>
              )}
            </section>
          ))}
          {rows.length > 0 && remote.status === 'short' && (
            <p className="xo-hit-note">{tx('ui.search.keep_typing')}</p>
          )}
          {rows.length === 0 && busy && (
            <div className="xo-blank" role="status">
              <Loader2 className="xo-spin" aria-hidden="true" />
              <strong>{tx('ui.search.searching')}</strong>
            </div>
          )}
          {rows.length === 0 && !busy && (
            <div className="xo-blank">
              <SearchIcon aria-hidden="true" />
              <strong>{tx("ui.search.nothing_matches_term_c5699329", { term: query })}</strong>
              <p>
                {remote.status === 'short' ? tx('ui.search.keep_typing') : tx('ui.search.nothing_found_detail')}
              </p>
            </div>
          )}
        </div>

        <div className="xo-modal-foot">
          <span><kbd>↑</kbd><kbd>↓</kbd> {tx("ui.search.to_move_d447e2ed")}</span>
          <span><kbd>↵</kbd> {tx("ui.search.to_open_e2c7f655")}</span>
          <span><kbd>{tx("ui.search.esc_1f7a4f9e")}</kbd> {tx("ui.search.to_close_0031ebe3")}</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
